<template>
  <div
    v-if="open"
    ref="tray"
    class="otm-tray"
    :style="shift ? { '--otm-tray-shift': `${shift}px` } : null"
    role="dialog"
    :aria-labelledby="titleId"
    @keydown="onKeydown"
  >
    <h2 :id="titleId" class="otm-tray-title">{{ $t('map.on_this_map_control.on_this_map') }}</h2>

    <button ref="clearAll" type="button" class="otm-clear-all" @click="$emit('clear-all', { keyboard: isKeyboardClick($event) })">
      {{ $t('map.on_this_map_control.clear_all_n', { count: total }) }}
    </button>

    <ul class="otm-rows">
      <li v-for="row in rows" :key="row.group" class="otm-row">
        <!-- Opens the Maps sheet on the tab where this group is switched on. -->
        <button
          type="button"
          class="otm-row-open"
          :aria-label="$t('map.on_this_map_control.open_group', { group: row.title, maps: $t('shared.maps') })"
          :aria-describedby="`${titleId}-${row.group}`"
          @click="$emit('open-group', row.group)"
        >
          <span class="otm-row-text">
            <span class="otm-row-title">{{ row.title }}</span>
            <span :id="`${titleId}-${row.group}`" class="otm-row-summary">{{ row.summary }}</span>
          </span>
          <span class="material-icons otm-row-chevron" aria-hidden="true">chevron_right</span>
        </button>
        <button
          type="button"
          class="otm-row-clear"
          :aria-label="$t('map.on_this_map_control.clear_group', { group: row.title })"
          @click="$emit('clear-group', row.group, { keyboard: isKeyboardClick($event) })"
        >
          {{ $t('map.on_this_map_control.clear') }}
        </button>
      </li>
    </ul>

    <p class="otm-footnote">{{ $t('map.on_this_map_control.tray_footnote') }}</p>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { OnThisMapGroup } from '../../../../utils/onThisMap';
import { focusableWithin, wrapFocus } from './focusTrap';

export interface OnThisMapRow {
  group: OnThisMapGroup;
  title: string;
  summary: string;
}

let nextId = 0;

export default defineComponent({
  name: 'OnThisMapTray',
  props: {
    open: { type: Boolean, default: false },
    total: { type: Number, required: true },
    rows: { type: Array as PropType<OnThisMapRow[]>, required: true },
    // How far the pill row is slid right (see MapButtons), so the tray stays under it.
    shift: { type: Number, default: 0 },
    // The control that opens the tray: a tap on it toggles rather than counting as outside.
    anchorSelector: { type: String, default: '.otm-segment' },
  },
  // close: { refocus } — refocus the segment, except after a tap elsewhere on the page.
  // clear-all / clear-group: { keyboard } — whether Enter/Space did it, which decides where
  // focus goes if the segment disappears.
  // open-group: a row's text was tapped — open the Maps sheet on that group's tab.
  emits: ['close', 'clear-all', 'clear-group', 'open-group'],
  data() {
    return { titleId: `otm-tray-title-${++nextId}` };
  },
  watch: {
    open: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.$nextTick(() => (this.$refs.clearAll as HTMLElement | undefined)?.focus());
          document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
        } else {
          document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
        }
      },
    },
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
  },
  methods: {
    // Enter/Space on a button fires a click with no pointer behind it (detail 0).
    isKeyboardClick(event: MouseEvent) {
      return event.detail === 0;
    },
    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.$emit('close', { refocus: true });
        return;
      }
      if (event.key === 'Tab') {
        const tray = this.$refs.tray as HTMLElement;
        const next = wrapFocus(focusableWithin(tray), document.activeElement, event.shiftKey);
        if (next) {
          event.preventDefault();
          next.focus();
        }
      }
    },
    onDocumentPointerDown(event: Event) {
      const target = event.target as Element | null;
      const tray = this.$refs.tray as HTMLElement | undefined;
      if (!target || !tray || tray.contains(target)) return;
      if (target.closest?.(this.anchorSelector)) return;
      this.$emit('close', { refocus: false });
    },
  },
});
</script>

<style scoped>
/* The tray hangs off the pill row, inside `.button-container`, whose buttons are all forced to
   `.75em !important` for the pills — hence the !important font sizes on the tray's buttons. */
.otm-tray {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  transform: translateX(var(--otm-tray-shift, 0px));
  width: 340px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  padding: 10px 14px;
  border-radius: 12px;
  background: #22242A;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 14px;
  text-align: left;
  white-space: normal;
}

.otm-tray-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
}

.otm-clear-all {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  margin-bottom: 6px;
  padding: 6px 16px;
  border: 0;
  border-radius: 999px;
  background: #FDDF00;
  color: #17181D;
  font-size: 14px !important;
  font-weight: 700;
  line-height: 1.2;
  white-space: normal;
}

.otm-clear-all:hover {
  background: #e8cc00;
}

.otm-rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.otm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 6px 0;
  border-top: 1px solid #434343;
}

.otm-row-open {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 40px;
  margin: 0 0 0 -6px;
  padding: 2px 4px 2px 6px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: inherit;
  text-align: left;
  white-space: normal;
}

.otm-row-open:hover {
  background: #2D2F35;
}

.otm-row-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.otm-row-chevron {
  flex: none;
  font-size: 18px;
  color: #ACB0BF;
}

.otm-row-title {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

/* Only the summary truncates; labels and buttons always show in full. */
.otm-row-summary {
  overflow: hidden;
  font-size: 12px;
  line-height: 1.3;
  color: #ACB0BF;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.otm-row-clear {
  flex: none;
  min-height: 36px;
  padding: 4px 16px;
  border: 0;
  border-radius: 999px;
  background: #2D2F35;
  color: #fff;
  font-size: 13px !important;
  font-weight: 600;
  white-space: nowrap;
}

.otm-row-clear:hover {
  background: #3a3c43;
}

.otm-clear-all:focus,
.otm-row-open:focus,
.otm-row-clear:focus {
  outline: none;
}

.otm-clear-all:focus-visible,
.otm-row-open:focus-visible,
.otm-row-clear:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.otm-footnote {
  margin: 8px 0 2px;
  font-size: 11px;
  line-height: 1.3;
  color: #ACB0BF;
  text-align: center;
}

/* Phones: full width with a 12px margin, no wider than 340px. */
@media (max-width: 420px) {
  .otm-tray {
    left: 12px;
    right: 12px;
    width: auto;
    max-width: 340px;
    transform: none;
  }
}
</style>
